import type {
  AmenityReservation,
  ApproveReservationRequest,
  BlockedSlotsResponse,
  CancelReservationRequest,
  CheckAvailabilityRequest,
  CheckAvailabilityResponse,
  CreateReservationRequest,
  RejectReservationRequest,
  UpdateReservationRequest,
} from '@/types/v2/reservation';
import axios from '@/lib/v2/axios';

// User/Resident endpoints
export async function getMyReservations(params: {
  include?: string;
  sort?: string;
} = {}): Promise<{ reservations: AmenityReservation[] }> {
  const response = await axios.get('/api/v2/reservations/my', { params });
  return response.data;
}

export async function getReservation(
  reservationId: string,
  params: { include?: string } = {},
): Promise<AmenityReservation> {
  try {
    const response = await axios.get(`/api/v2/reservations/${reservationId}`, {
      params,
    });
    // Laravel JsonResource wraps the response in { data: {...} }
    return response.data?.data ?? response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error('Reservation not found');
    }
    throw error;
  }
}

export async function createReservation(
  data: CreateReservationRequest,
): Promise<AmenityReservation> {
  const response = await axios.post('/api/v2/reservations', data);
  return response.data;
}

export async function createReservationCheckout(
  data: CreateReservationRequest,
): Promise<{ checkout_url: string; session_id: string }> {
  const response = await axios.post('/api/v2/reservations/checkout', data);
  return response.data;
}

export async function updateReservation(
  reservationId: string,
  data: UpdateReservationRequest,
): Promise<AmenityReservation> {
  const response = await axios.put(`/api/v2/reservations/${reservationId}`, data);
  return response.data;
}

export async function cancelReservation(
  reservationId: string,
  data: CancelReservationRequest,
): Promise<AmenityReservation> {
  const response = await axios.post(
    `/api/v2/reservations/${reservationId}/cancel`,
    data,
  );
  return response.data;
}

// Manager endpoints
export async function getCommunityReservations(
  communityId: string,
  params: {
    include?: string;
    filter?: Record<string, any>;
    sort?: string;
  } = {},
): Promise<{ reservations: AmenityReservation[] }> {
  const response = await axios.get(
    `/api/v2/communities/${communityId}/reservations`,
    { params },
  );
  return response.data;
}

export async function getPendingReservations(
  communityId: string,
  params: { include?: string } = {},
): Promise<{ pending_reservations: AmenityReservation[] }> {
  const response = await axios.get(
    `/api/v2/communities/${communityId}/reservations/pending`,
    { params },
  );
  return response.data;
}

export async function approveReservation(
  communityId: string,
  reservationId: string,
  data: ApproveReservationRequest = {},
): Promise<AmenityReservation> {
  const response = await axios.post(
    `/api/v2/communities/${communityId}/reservations/${reservationId}/approve`,
    data,
  );
  return response.data;
}

export async function rejectReservation(
  communityId: string,
  reservationId: string,
  data: RejectReservationRequest,
): Promise<AmenityReservation> {
  const response = await axios.post(
    `/api/v2/communities/${communityId}/reservations/${reservationId}/reject`,
    data,
  );
  return response.data;
}

export async function getCommunityReservation(
  communityId: string,
  reservationId: string,
  params: { include?: string } = {},
): Promise<AmenityReservation> {
  const response = await axios.get(
    `/api/v2/communities/${communityId}/reservations/${reservationId}`,
    { params },
  );
  // Laravel JsonResource wraps the response in { data: {...} }
  return response.data?.data ?? response.data;
}

export async function updateCommunityReservation(
  communityId: string,
  reservationId: string,
  data: UpdateReservationRequest,
): Promise<AmenityReservation> {
  const response = await axios.put(
    `/api/v2/communities/${communityId}/reservations/${reservationId}`,
    data,
  );
  return response.data;
}

export async function deleteCommunityReservation(
  communityId: string,
  reservationId: string,
): Promise<void> {
  await axios.delete(`/api/v2/communities/${communityId}/reservations/${reservationId}`);
}

// Availability checking (Q2)
export async function checkAvailability(
  data: CheckAvailabilityRequest,
): Promise<CheckAvailabilityResponse> {
  const response = await axios.post('/api/v2/reservations/check-availability', data);
  return response.data;
}

export async function getBlockedSlots(
  amenityId: string,
  startDate: string,
  endDate: string,
): Promise<BlockedSlotsResponse> {
  const response = await axios.get(`/api/v2/reservations/${amenityId}/blocked-slots`, {
    params: {
      start_date: startDate,
      end_date: endDate,
    },
  });
  return response.data;
}

export async function chargeDamage(
  communityId: string,
  reservationId: string,
  data: { amount: number; notes: string },
): Promise<AmenityReservation> {
  const response = await axios.post(
    `/api/v2/communities/${communityId}/reservations/${reservationId}/charge-damage`,
    data,
  );
  return response.data;
}

export async function payFee(
  reservationId: string,
): Promise<AmenityReservation> {
  const response = await axios.post(`/api/v2/reservations/${reservationId}/pay-fee`);
  return response.data?.data ?? response.data;
}

export async function refundDeposit(
  communityId: string,
  reservationId: string,
  data: { amount?: number; reason?: string } = {},
): Promise<AmenityReservation> {
  const response = await axios.post(
    `/api/v2/communities/${communityId}/reservations/${reservationId}/refund-deposit`,
    data,
  );
  return response.data;
}

export async function refundFee(
  communityId: string,
  reservationId: string,
  data: { reason?: string } = {},
): Promise<AmenityReservation> {
  const response = await axios.post(
    `/api/v2/communities/${communityId}/reservations/${reservationId}/refund-fee`,
    data,
  );
  return response.data;
}





